// The close safety net. Stage 4 item 5. Words: JSP_CLOSE_NET_WORDS.md, mockup MOCKUP_JSP_CLOSE_NET.html
// (approved by Robert 2026-10-09). Decision 59, JOB_CLOSE_WORDS.md (2026-10-08).
// Pure: the server steps, the Dashboard card and the unit tests share it. Nothing here reads or writes the database.
//
// Rules:
// * A JSP stays open after Submit until it is closed.
// * The app closes it at his next Submit, or 12 hours after Submit (an hourly job). Drafts are never touched.
// * The Dashboard card asks how he left it: one tap on energized jobs. On de-energized jobs it opens the
//   close pill, so isolation and the re-energize checks are always answered (Robert, 2026-10-09).
// * After an auto-close he can still answer for 7 days. The record keeps that the app closed it.
// * A close never changes serviceability, NOC or IE on the asset.

import { CLOSE_LEFT, answersFrom, findings, type JspClose, type JspDraft, type JspState } from "./jsp";
import { SITE_TIME_ZONE } from "./training-server";

/** Set in code for now (Robert, 2026-10-09). Program Settings later if a site's shift pattern needs it. */
export const AUTO_CLOSE_HOURS = 12;
export const LATE_ANSWER_DAYS = 7;
/** Stamped on every close the app makes. */
export const APP_CLOSER = "SafeTag";

/** The as-left answer, as the QEW reads it. Renamed by Robert 2026-10-09 (was "Worse. Needs Repair"). */
export const LEFT_AS_LABEL: Record<(typeof CLOSE_LEFT)[number], string> = {
  same: "As I Found It",
  fixed: "Fixed What I Found",
  worse: "Needs Repair - Action Required",
};
export const REPAIR_NOTE_PROMPT = "What needs repair?";

export type AutoCloseHow = "next" | "hours";

const HOUR = 3_600_000;
const DAY = 86_400_000;

/** 12 hours or more since Submit. */
export function autoCloseDue(submittedAt: string, nowIso: string): boolean {
  const s = Date.parse(submittedAt);
  return Number.isFinite(s) && Date.parse(nowIso) - s >= AUTO_CLOSE_HOURS * HOUR;
}

/** The fields an auto-close writes. As-left stays blank until he answers. */
export function autoCloseFields(how: AutoCloseHow, today: string, nowIso: string) {
  return {
    status: "closed" as const,
    closedHow: how === "next" ? "App, next JSP" : "App, 12 hours",
    isAutoClosed: true,
    autoCloseLine: how === "next" ? "Closed by the app at his next JSP. As-left not reported." : "Closed by the app after 12 hours. As-left not reported.",
    closedByQewId: APP_CLOSER,
    closedByName: APP_CLOSER,
    closedOn: today,
    closedAt: nowIso,
    leftAs: null,
  };
}

/** A JSP as the close net reads it. */
export interface CloseRow {
  jspNumber: string;
  assetId: string;
  taskName: string;
  status: string;
  submittedOn: string;
  submittedAt: string;
  isDeEnergized: boolean;
  isAutoClosed: boolean;
  leftAs: string | null;
  closedAt: string | null;
  /** The Field Assessment recorded a finding. Fixed What I Found shows only then. */
  hasFindings: boolean;
}

/** An auto-closed JSP still waiting for his answer, inside the 7 days. */
export function awaitsLateAnswer(r: CloseRow, nowIso: string): boolean {
  if (r.status !== "closed" || !r.isAutoClosed || r.leftAs || !r.closedAt) return false;
  return Date.parse(nowIso) - Date.parse(r.closedAt) <= LATE_ANSWER_DAYS * DAY;
}

/** The Dashboard card: his newest open JSP, or his newest auto-closed JSP still waiting. Null: no card. */
export function closeCardOf(rows: readonly CloseRow[], nowIso: string): CloseRow | null {
  const live = rows.filter((r) => r.status === "open" || awaitsLateAnswer(r, nowIso));
  return live.sort((a, b) => b.submittedAt.localeCompare(a.submittedAt))[0] ?? null;
}

/** Why a one-tap card answer is refused. Null when it can be saved. De-energized jobs go to the close pill. */
export function cardProblem(r: Pick<CloseRow, "isDeEnergized" | "hasFindings">, leftAs: unknown): string | null {
  if (r.isDeEnergized) return "Open the JSP to close it. Tell us where you left the isolation.";
  if (typeof leftAs !== "string" || !(CLOSE_LEFT as readonly string[]).includes(leftAs)) return "How did you leave the equipment?";
  if (leftAs === "fixed" && !r.hasFindings) return "Nothing was found at the Field Assessment to fix.";
  return null;
}

/** The fields a card answer writes. On an open JSP it closes it; on an auto-closed one it fills in how he left it. */
export function cardAnswerFields(leftAs: JspClose["leftAs"], note: string, closing: boolean, who: { badgeId: string; holderName: string }, today: string, nowIso: string) {
  const answer = { leftAs, leftAsNote: leftAs === "worse" ? note.trim().slice(0, 1000) : "", asLeftReportedOn: today, asLeftReportedAt: nowIso };
  return closing ? { ...answer, status: "closed" as const, closedHow: "Dashboard card", closedByQewId: who.badgeId, closedByName: who.holderName, closedOn: today, closedAt: nowIso } : answer;
}

/** "10/09 19:20" for the banner, in site time. */
export function closedAtText(iso: string, timeZone = SITE_TIME_ZONE): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("month")}/${get("day")} ${get("hour") === "24" ? "00" : get("hour")}:${get("minute")}`;
}

/** "10/09" for the card line. */
export function usShort(iso: string): string {
  const [, m, d] = iso.split("-");
  return m && d ? `${m}/${d}` : iso;
}

/** A JSP doc as the close net reads it. */
export function closeRowOf(id: string, d: Record<string, unknown>): CloseRow {
  const answers = answersFrom(d.answers);
  const str = (v: unknown) => (typeof v === "string" ? v : "");
  return {
    jspNumber: id,
    assetId: str(d.assetId),
    taskName: str(d.taskName),
    status: str(d.status),
    submittedOn: str(d.submittedOn),
    submittedAt: str(d.submittedAt),
    isDeEnergized: d.isDeEnergized === true,
    isAutoClosed: d.isAutoClosed === true,
    leftAs: typeof d.leftAs === "string" && d.leftAs ? d.leftAs : null,
    closedAt: typeof d.closedAt === "string" ? d.closedAt : null,
    hasFindings: answers ? findings({ cond: answers.cond } as JspState).length > 0 : false,
  };
}

/** The saved answers turned back into a draft, so the JSP page can reopen at the close pill. */
export function draftFromRecord(d: Record<string, unknown>): JspDraft | null {
  const answers = answersFrom(d.answers);
  if (!answers) return null;
  return { answers, energyPicked: answers.otherEnergy.map((o) => o.type), phase: "Job", fitConfirmed: true };
}

/** What the Dashboard card shows. Same shape on the server and the page. */
export interface CloseCard {
  jspNumber: string;
  assetId: string;
  taskName: string;
  /** "10/09" */
  submittedDay: string;
  isDeEnergized: boolean;
  hasFindings: boolean;
  /** "10/09 19:20" when the app already closed it. Null while it is open. */
  autoAt: string | null;
}

export function closeCardViewOf(r: CloseRow): CloseCard {
  return {
    jspNumber: r.jspNumber,
    assetId: r.assetId,
    taskName: r.taskName,
    submittedDay: usShort(r.submittedOn),
    isDeEnergized: r.isDeEnergized,
    hasFindings: r.hasFindings,
    autoAt: r.status === "closed" && r.closedAt ? closedAtText(r.closedAt) : null,
  };
}
